/**
 * The billing notice above every page: read-only, a failed payment, or the trial's last week.
 * `shellNotice` decides which; this only words it and links to the billing page, where every one
 * of them is resolved. On the billing page itself it stays out of the way, since that page says
 * the same thing at more length.
 */

import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/ui/Field";
import type { Me } from "@/features/auth/session";
import { formatMomentDate } from "@/lib/dates";
import { shellNotice } from "./status";
import { UpgradeLink } from "./UpgradeLink";

export function BillingNotice({ workspace }: { workspace: NonNullable<Me["workspace"]> }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const notice = shellNotice(workspace);
  const target = notice?.kind === "photos" ? "/settings/data" : "/settings/billing";
  if (!notice || pathname === target) return null;

  let text: string;
  let action = t("billing.seePlans");
  switch (notice.kind) {
    case "readOnly":
      text = notice.trialEnd
        ? t("app.readOnly.trialEnded", {
            date: formatMomentDate(notice.trialEnd, workspace.timezone),
          })
        : t("app.readOnly.lapsed");
      break;
    case "pastDue":
      text = t("billing.pastDueBanner");
      action = t("billing.fixPayment");
      break;
    case "photos":
      text = t("data.photos.banner", { count: notice.count });
      action = t("data.title");
      break;
    case "trial":
      text =
        notice.days === 0
          ? t("billing.trialBannerToday")
          : t("billing.trialBanner", { count: notice.days });
      break;
  }

  return (
    <div className="mb-4">
      <Notice tone="info">
        {text} <UpgradeLink label={action} to={target} />
      </Notice>
    </div>
  );
}
